import React, { useEffect, useState, useCallback } from "react";
import api from "../../lib/api";
import { site, trips } from "../../data/mock";
import { Loader2, Phone, MessageCircle, Mail, Trash2, Users, X, Calendar, User, Anchor, MessageSquare, DollarSign, MapPin, PackageCheck } from "lucide-react";

const STATUSES = ["new", "contacted", "booked", "closed"];
const badge = { new: "bg-[#f5a524] text-[#0a1f33]", contacted: "bg-[#1c6ba3] text-white", booked: "bg-[#0e9488] text-white", closed: "bg-white/10 text-slate-400" };
const waNum = (p) => (p || "").replace(/[^0-9]/g, "");
const waMsg = (l) => {
  const first = (l.name || "").trim().split(" ")[0] || "there";
  const realTrip = l.trip && !["Plan Your Trip", "General inquiry"].includes(l.trip);
  let msg = `Hi ${first}! 🎣 It's the Hog Tide Fishing crew from San Juan del Sur — thanks so much for reaching out`;
  msg += realTrip ? ` about the ${l.trip}!` : "!";
  const sp = Array.isArray(l.species) ? l.species.join(", ") : l.species;
  if (sp) msg += ` ${sp} is a fantastic target — our captain loves chasing those.`;
  msg += ` What dates are you thinking? We'll check the calendar and get you out on the water. 🌊`;
  return encodeURIComponent(msg);
};

const first = (l) => (l.name || "").trim().split(" ")[0] || "there";
const MIN_PRICE = Math.min(...trips.map((t) => t.price));
const realTripName = (l) => (l.trip && !["Plan Your Trip", "General inquiry"].includes(l.trip)) ? l.trip : null;
const tripPrice = (l) => { const t = trips.find((x) => x.name === l.trip); return t ? t.price : null; };

export const TEMPLATE_META = [
  { id: "pricing", label: "Pricing", icon: DollarSign },
  { id: "availability", label: "Availability", icon: Calendar },
  { id: "directions", label: "Directions", icon: MapPin },
  { id: "included", label: "What's Included", icon: PackageCheck },
  { id: "confirm", label: "Confirm Booking", icon: Anchor },
];

export const DEFAULT_TEMPLATES = {
  pricing: "Hi {name}! The {trip} is {price} for a private charter — the whole boat is yours (up to 12 guests) with fuel, all gear, licenses and crew included. Want me to hold a date for you?",
  availability: "Hi {name}! We'd love to get you on the water. What date (or a couple of options) are you looking at? We fish daily — I'll check the calendar right away and confirm your spot.",
  directions: "Hi {name}! We depart from San Juan del Sur, Nicaragua — here's the meeting point: {maps} . We'll confirm the exact dock and time once your date is set.",
  included: "Hi {name}! Every Hog Tide charter includes all fishing gear & tackle, bait, licenses, ice and bottled water, plus your expert local crew. Just bring sunscreen, a hat and your camera!",
  confirm: "Hi {name}! To lock in your {trip} we just need a small deposit to reserve the boat, with the balance due on the day. Want me to send you the booking link?",
};

// Replace {tokens} with this lead's real values.
const fillTemplate = (text, l) => {
  const price = tripPrice(l);
  return (text || "")
    .replace(/\{name\}/g, first(l))
    .replace(/\{trip\}/g, realTripName(l) || "trip")
    .replace(/\{price\}/g, price ? `$${price}` : `from $${MIN_PRICE}`)
    .replace(/\{min_price\}/g, `$${MIN_PRICE}`)
    .replace(/\{maps\}/g, site.social.google);
};

const LeadModal = ({ lead, onClose, onStatus, templates }) => {
  if (!lead) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onClick={onClose} data-testid="lead-detail-modal">
      <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#0e2740] p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="font-display text-2xl text-white" data-testid="lead-detail-name">{lead.name}</h2>
              <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${badge[lead.status]}`}>{lead.status}</span>
              <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-300 capitalize">{lead.source}</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">{new Date(lead.created_at).toLocaleString()}</p>
          </div>
          <button onClick={onClose} className="rounded-lg bg-white/5 p-2 text-slate-300 hover:bg-white/10" data-testid="lead-detail-close"><X className="h-4 w-4" /></button>
        </div>

        {/* One-tap actions */}
        <div className="grid grid-cols-2 gap-3 mt-5">
          <a href={`tel:${lead.phone}`} data-testid="lead-detail-call" className="flex items-center justify-center gap-2 rounded-xl bg-[#1c6ba3] text-white font-heading py-3.5 hover:bg-[#175a8c] transition">
            <Phone className="h-5 w-5" /> Call
          </a>
          <a href={`https://wa.me/${waNum(lead.phone)}?text=${waMsg(lead)}`} target="_blank" rel="noreferrer" data-testid="lead-detail-whatsapp" className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] text-[#04361a] font-heading py-3.5 hover:brightness-95 transition">
            <MessageCircle className="h-5 w-5" /> WhatsApp
          </a>
        </div>
        <a href={`mailto:${lead.email}`} className="mt-3 flex items-center justify-center gap-2 rounded-xl bg-white/5 text-slate-200 font-heading py-3 hover:bg-white/10 transition"><Mail className="h-4 w-4" /> Email {lead.email}</a>

        {/* Quick reply templates */}
        <div className="mt-4">
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-2 font-heading">Quick replies · one tap to WhatsApp</p>
          <div className="grid grid-cols-2 gap-2" data-testid="lead-quick-replies">
            {TEMPLATE_META.map((m) => {
              const text = (templates && templates[m.id]) || DEFAULT_TEMPLATES[m.id];
              return (
                <a key={m.id} href={`https://wa.me/${waNum(lead.phone)}?text=${encodeURIComponent(fillTemplate(text, lead))}`} target="_blank" rel="noreferrer" data-testid={`qr-${m.id}`} className="flex items-center gap-2 rounded-xl bg-[#25D366]/12 border border-[#25D366]/25 text-[#7fe6a3] px-3 py-2.5 text-sm hover:bg-[#25D366]/20 transition">
                  <m.icon className="h-4 w-4 shrink-0" /> {m.label}
                </a>
              );
            })}
          </div>
        </div>

        {/* Details */}
        <div className="mt-5 space-y-2.5 text-sm">
          <div className="flex items-center gap-2 text-slate-300"><Phone className="h-4 w-4 text-slate-500" /> {lead.phone || "—"}</div>
          <div className="flex items-center gap-2 text-slate-300"><Anchor className="h-4 w-4 text-slate-500" /> {lead.trip || "General inquiry"}</div>
          {lead.date && <div className="flex items-center gap-2 text-slate-300"><Calendar className="h-4 w-4 text-slate-500" /> {lead.date}</div>}
          {lead.guests ? <div className="flex items-center gap-2 text-slate-300"><User className="h-4 w-4 text-slate-500" /> {lead.guests} guests</div> : null}
          {lead.species && lead.species.length > 0 && (
            <div className="flex flex-wrap gap-1.5 pt-1">{lead.species.map((s) => <span key={s} className="rounded-full bg-[#f5a524]/15 text-[#f5a524] px-2.5 py-0.5 text-xs">{s}</span>)}</div>
          )}
          {lead.deposit ? <div className="text-slate-300">Deposit: ${lead.deposit} ({lead.method})</div> : null}
          {lead.message && (
            <div className="rounded-xl bg-white/5 p-3 text-slate-300 italic flex gap-2"><MessageSquare className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" /> “{lead.message}”</div>
          )}
        </div>

        {/* Status */}
        <div className="mt-5 flex items-center gap-2">
          <span className="text-xs text-slate-400">Status:</span>
          <select value={lead.status} onChange={(e) => onStatus(lead.id, e.target.value)} className="h-9 rounded-lg bg-white/5 border border-white/15 px-2 text-xs text-white flex-1" data-testid="lead-detail-status">
            {STATUSES.map((s) => (<option key={s} value={s} className="text-black capitalize">{s}</option>))}
          </select>
        </div>
      </div>
    </div>
  );
};

const AdminLeads = ({ onChange, focusLeadId, onFocusHandled }) => {
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [templates, setTemplates] = useState(null);

  useEffect(() => { api.getTemplates().then((r) => setTemplates(r.templates)).catch(() => setTemplates({})); }, []);

  const load = useCallback(() => api.adminLeads(filter).then(setLeads).catch(() => setLeads([])), [filter]);
  useEffect(() => { setLeads(null); load(); }, [load]);

  // Deep-link: open a specific lead's details (from a notification).
  useEffect(() => {
    if (!focusLeadId) return;
    let done = false;
    const open = async () => {
      let lead = (leads || []).find((l) => l.id === focusLeadId);
      if (!lead) {
        try { lead = await api.getLead(focusLeadId); } catch (e) { lead = null; }
      }
      if (!done && lead) setSelected(lead);
      onFocusHandled && onFocusHandled();
    };
    open();
    return () => { done = true; };
  }, [focusLeadId, leads, onFocusHandled]);

  const setStatus = async (id, status) => {
    await api.updateLead(id, status);
    setSelected((s) => (s && s.id === id ? { ...s, status } : s));
    load();
    onChange && onChange();
  };
  const remove = async (id) => { await api.deleteLead(id); setSelected(null); load(); onChange && onChange(); };

  return (
    <div>
      <h1 className="font-display text-3xl text-white mb-1">Leads</h1>
      <p className="text-slate-400 text-sm mb-5">Every booking request & inquiry. Tap a lead to open full details — then call or WhatsApp them back in one tap.</p>
      <div className="flex gap-2 mb-5 flex-wrap">
        {["all", ...STATUSES].map((s) => (<button key={s} onClick={() => setFilter(s)} className={`rounded-full px-4 py-1.5 text-xs font-heading capitalize ${filter === s ? "bg-[#f5a524] text-[#0a1f33]" : "bg-[#0e2740] text-slate-300 border border-white/10"}`}>{s}</button>))}
      </div>
      {leads === null ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#f5a524]" /></div>
      ) : leads.length === 0 ? (
        <div className="text-center py-20 text-slate-500"><Users className="h-10 w-10 mx-auto mb-2 opacity-40" />No leads yet.</div>
      ) : (
        <div className="space-y-3">
          {leads.map((l) => (
            <div key={l.id} onClick={() => setSelected(l)} data-testid={`lead-row-${l.id}`} className="cursor-pointer rounded-2xl border border-white/10 bg-[#0e2740] p-4 md:flex items-center gap-4 hover:border-[#f5a524]/40 transition">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading text-lg text-white">{l.name}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] uppercase ${badge[l.status]}`}>{l.status}</span>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 text-[10px] text-slate-300 capitalize">{l.source}</span>
                </div>
                <p className="text-sm text-slate-300 mt-1">{l.trip || "General inquiry"}{l.date ? ` · ${l.date}` : ""}{l.guests ? ` · ${l.guests} guests` : ""}{l.deposit ? ` · $${l.deposit} deposit (${l.method})` : ""}</p>
                {l.message && <p className="text-xs text-slate-400 mt-1 italic">“{l.message}”</p>}
                <p className="text-[11px] text-slate-500 mt-1">{new Date(l.created_at).toLocaleString()}</p>
              </div>
              <div className="flex items-center gap-2 mt-3 md:mt-0" onClick={(e) => e.stopPropagation()}>
                <a href={`tel:${l.phone}`} className="rounded-lg bg-[#1c6ba3]/20 text-[#5cb3e6] p-2.5 hover:bg-[#1c6ba3]/40" title={l.phone}><Phone className="h-4 w-4" /></a>
                <a href={`https://wa.me/${waNum(l.phone)}?text=${waMsg(l)}`} target="_blank" rel="noreferrer" className="rounded-lg bg-[#25D366]/20 text-[#25D366] p-2.5 hover:bg-[#25D366]/40"><MessageCircle className="h-4 w-4" /></a>
                <a href={`mailto:${l.email}`} className="rounded-lg bg-white/5 text-slate-300 p-2.5 hover:bg-white/10"><Mail className="h-4 w-4" /></a>
                <select value={l.status} onChange={(e) => setStatus(l.id, e.target.value)} className="h-9 rounded-lg bg-white/5 border border-white/15 px-2 text-xs text-white">
                  {STATUSES.map((s) => (<option key={s} value={s} className="text-black capitalize">{s}</option>))}
                </select>
                <button onClick={() => remove(l.id)} className="rounded-lg bg-red-500/15 text-red-400 p-2.5 hover:bg-red-500/30"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-500 mt-4">Phone: <a href={`tel:${site.phoneRaw}`} className="text-[#f5a524]">{site.phone}</a></p>
      <LeadModal lead={selected} onClose={() => setSelected(null)} onStatus={setStatus} templates={templates} />
    </div>
  );
};

export default AdminLeads;
